import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Building2, MapPin, Bell, Menu, ChevronDown, User,
  LayoutDashboard, Heart, LogOut, ChevronRight, X, Loader2,
  Building, Store, Warehouse as WarehouseIcon, Trees, Compass, CheckCircle2, Home as HomeIcon, Landmark, BookOpen, Calculator, Award
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import NotificationBell from "@/components/NotificationBell";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";
import { loadDiscoveryCities } from "@/lib/discovery";

/* -------------------- Brand Accent -------------------- */
const BRAND = "#B8842F";

/* -------------------- Mega Menu Data -------------------- */
const MEGA = {
  Buy: {
    columns: [
      {
        title: "Buy a Home",
        items: [
          { label: "Flats/Apartments", to: "/properties?listing_type=SELL&property_types=FLAT" },
          { label: "Independent / Builder Floors", to: "/properties?listing_type=SELL&property_types=HOUSE" },
          { label: "Independent House / Villa", to: "/properties?listing_type=SELL&property_types=HOUSE%2CVILLA" },
          { label: "Farm House", to: "/properties?listing_type=SELL&property_types=HOUSE&q=Farm+House" },
          { label: "Serviced Apartments", to: "/properties?listing_type=SELL&property_types=FLAT&q=Serviced+Apartment" },
        ],
      },
      {
        title: "Land / Plot",
        items: [
          { label: "Verified Plots", to: "/plots?category=PLOT&verified_owner=true" },
          { label: "Residential Plots", to: "/plots?category=PLOT" },
          { label: "Agricultural Land", to: "/plots?category=LAND" },
          { label: "Plots on Demand", to: "/plot-on-demand" },
        ],
      },
      {
        title: "Commercial",
        items: [
          { label: "Office Space", to: "/properties?category=COMMERCIAL&q=Office+Space" },
          { label: "Shop / Showroom", to: "/properties?category=COMMERCIAL&q=Shop" },
          { label: "Warehouse / Godown", to: "/properties?category=COMMERCIAL&q=Warehouse" },
        ],
      },
      {
        title: "Popular Cities",
        dynamicCities: true,
      },
    ],
    insights: [
      { label: "Lowest Price First", to: "/properties?sort=price_asc" },
      { label: "Real Estate Forums", to: "/blog" },
      { label: "Check Price Trends", to: "/blog" },
      { label: "Maps", to: "/properties" },
    ],
  },
  Rent: {
    columns: [
      {
        title: "Rent a Home",
        items: [
          { label: "Flats/Apartments", to: "/properties?listing_type=RENT&property_types=FLAT" },
          { label: "Independent House / Villa", to: "/properties?listing_type=RENT&property_types=HOUSE%2CVILLA" },
          { label: "PG / Co-living", to: "/properties?listing_type=RENT&property_types=PG_HOSTEL" },
        ],
      },
      {
        title: "Furnishing",
        items: [
          { label: "Fully Furnished", to: "/properties?listing_type=RENT&furnished=FULLY_FURNISHED" },
          { label: "Semi Furnished", to: "/properties?listing_type=RENT&furnished=SEMI_FURNISHED" },
          { label: "Unfurnished", to: "/properties?listing_type=RENT&furnished=UNFURNISHED" },
        ],
      },
      {
        title: "Tenant Type",
        items: [
          { label: "All Rental Homes", to: "/properties?listing_type=RENT" },
          { label: "Rental Guide", to: "/blog" },
          { label: "Rent Calculator", to: "/emi-calculator" },
        ],
      },
    ],
    insights: [
      { label: "Rent Calculator", to: "/emi-calculator" },
      { label: "Rental Yields", to: "/blog" },
      { label: "Tenant Guide", to: "/blog" },
    ],
  },
  "New Launch": {
    columns: [
      {
        title: "Featured Projects",
        items: [
          { label: "New Launch Properties", to: "/properties?construction_status=NEW_LAUNCH" },
          { label: "Ready to Move", to: "/properties?construction_status=READY_TO_MOVE" },
          { label: "Under Construction", to: "/properties?construction_status=UNDER_CONSTRUCTION" },
        ],
      },
      {
        title: "By City",
        dynamicCities: true,
        query: { construction_status: "NEW_LAUNCH" },
      },
    ],
    insights: [
      { label: "Builder Reviews", to: "/blog" },
      { label: "Document Verified", to: "/properties?verified_documents=true" },
    ],
  },
  Commercial: {
    columns: [
      {
        title: "Buy Commercial",
        items: [
          { label: "Office Space", to: "/properties?category=COMMERCIAL&listing_type=SELL&q=Office+Space" },
          { label: "Shop / Showroom", to: "/properties?category=COMMERCIAL&listing_type=SELL&q=Shop" },
          { label: "Warehouse", to: "/properties?category=COMMERCIAL&listing_type=SELL&q=Warehouse" },
        ],
      },
      {
        title: "Lease Commercial",
        items: [
          { label: "Office Space", to: "/properties?category=COMMERCIAL&listing_type=RENT&q=Office+Space" },
          { label: "Co-working", to: "/properties?category=COMMERCIAL&q=Co-working" },
        ],
      },
    ],
    insights: [
      { label: "Investment Insights", to: "/blog" },
    ],
  },
  "Plot/Land": {
    columns: [
      {
        title: "Residential Plot/Land",
        items: [
          { label: "Buy a Plot", to: "/plots?category=PLOT" },
          { label: "Verified Plots", to: "/plots?category=PLOT&verified_owner=true" },
          { label: "Plots on Demand", to: "/plot-on-demand" },
        ],
      },
      {
        title: "Land & Farmland",
        items: [
          { label: "Agricultural Land", to: "/plots?category=LAND" },
          { label: "Corner Plots", to: "/plots?category=PLOT&corner_plot=true" },
          { label: "Registry Ready", to: "/plots?registry_available=true" },
        ],
      },
    ],
    insights: [
      { label: "Land Price Trends", to: "/blog" },
    ],
  },
  Projects: {
    columns: [
      {
        title: "Browse Projects",
        items: [
          { label: "All Projects", to: "/properties?q=Project" },
          { label: "Top Builders", to: "/offices" },
        ],
      },
    ],
    insights: [],
  },
};

const NAV_ITEMS = ["Buy", "Rent", "New Launch", "Commercial", "Plot/Land", "Projects"];

const getSubMenuIcon = (label) => {
  const l = label.toLowerCase();
  if (l.includes("office") || l.includes("flat") || l.includes("apartment")) {
    return <Building className="w-3.5 h-3.5 text-blue-500 shrink-0" />;
  }
  if (l.includes("shop") || l.includes("showroom")) {
    return <Store className="w-3.5 h-3.5 text-amber-500 shrink-0" />;
  }
  if (l.includes("warehouse")) {
    return <WarehouseIcon className="w-3.5 h-3.5 text-slate-500 shrink-0" />;
  }
  if (l.includes("plot") || l.includes("land") || l.includes("farm")) {
    return <Trees className="w-3.5 h-3.5 text-emerald-500 shrink-0" />;
  }
  if (l.includes("recommend") || l.includes("search") || l.includes("find")) {
    return <Compass className="w-3.5 h-3.5 text-indigo-500 shrink-0" />;
  }
  if (l.includes("verified")) {
    return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />;
  }
  if (l.includes("project") || l.includes("all")) {
    return <HomeIcon className="w-3.5 h-3.5 text-sky-500 shrink-0" />;
  }
  if (l.includes("top builder") || l.includes("office")) {
    return <Landmark className="w-3.5 h-3.5 text-violet-500 shrink-0" />;
  }
  if (l.includes("emi")) {
    return <Calculator className="w-3.5 h-3.5 text-cyan-500 shrink-0" />;
  }
  if (l.includes("guide") || l.includes("blog") || l.includes("trends")) {
    return <BookOpen className="w-3.5 h-3.5 text-teal-500 shrink-0" />;
  }
  return <Award className="w-3.5 h-3.5 text-blue-500 shrink-0" />;
};

function MegaPanel({ item, onClose, cities }) {
  const data = MEGA[item];
  if (!data) return null;

  const hasInsights = data.insights && data.insights.length > 0;

  return (
    <div
      className="pointer-events-auto"
      style={{
        width: "max-content",
        maxWidth: "92vw"
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.97 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        className="relative rounded-2xl overflow-hidden p-5 text-left border shadow-2xl backdrop-blur-2xl flex gap-6 bg-[#0b1329]/95 border-white/10 text-white shadow-black/40"
        data-testid={`mega-${item.replace(/[\s/]/g, "-").toLowerCase()}`}
      >
        {/* Top Accent Gradient Bar */}
        <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-[#B8842F] via-[#D5A74D] to-[#8A6122]" />

        <div className="flex gap-6">
          {/* Main columns */}
          <div className="flex gap-6">
            {data.columns.map((col) => {
              const items = col.dynamicCities
                ? cities.slice(0, 5).map((city) => {
                    const params = new URLSearchParams(col.query || {});
                    params.set("city", city.query_value || city.name);
                    return { label: city.name, to: `/properties?${params.toString()}` };
                  })
                : col.items;
              return <div key={col.title} className="min-w-[140px] max-w-[180px]">
                <h4 className="text-[10px] font-extrabold uppercase tracking-[0.15em] mb-3 pb-2 border-b text-slate-400 border-white/10">
                  {col.title}
                </h4>
                <ul className="space-y-1">
                  {items.map((it) => {
                    const isPlotOnDemand = it.label === "Plots on Demand";
                    return (
                      <li key={it.label}>
                        <Link
                          to={it.to}
                          onClick={onClose}
                          className={`text-[13px] font-medium transition-all inline-flex items-center gap-2 py-1.5 px-2 -mx-2 rounded-lg w-full ${
                            isPlotOnDemand
                              ? "text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 shadow-[0_0_15px_rgba(16,185,129,0.2)] animate-pulse"
                              : "text-slate-300 hover:text-emerald-400 hover:bg-white/5"
                          }`}
                          data-testid={`mega-link-${it.label.replace(/[\s/]/g, "-").toLowerCase()}`}
                        >
                          {getSubMenuIcon(it.label)}
                          <span className="whitespace-nowrap">{it.label}</span>
                          {isPlotOnDemand && (
                            <span className="ml-auto w-2 h-2 rounded-full bg-emerald-400 animate-ping shrink-0" />
                          )}
                          {!isPlotOnDemand && (
                            <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition shrink-0 ml-auto" />
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>;
            })}
          </div>

          {/* Quick Actions / Insights sidebar */}
          {hasInsights && (
            <div className="min-w-[155px] border rounded-xl p-4 self-start bg-white/5 border-white/5">
              <h4 className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400 mb-3">Quick Actions</h4>
              <ul className="space-y-1.5">
                {data.insights.map((it) => (
                  <li key={it.label}>
                    <Link
                      to={it.to}
                      onClick={onClose}
                      className="flex items-center justify-between text-[13px] font-bold transition py-1.5 px-2 -mx-2 rounded-lg text-slate-200 hover:text-emerald-400 hover:bg-white/5"
                      data-testid={`mega-insight-${it.label.replace(/[\s/]/g, "-").toLowerCase()}`}
                    >
                      <span className="whitespace-nowrap">{it.label}</span>
                      <ChevronRight className="w-3.5 h-3.5 opacity-40" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/* -------------------- Component -------------------- */
export default function Navbar() {
  const { user, logout, isLoggingOut } = useAuth();
  const [hovered, setHovered] = useState(null);
  const [city, setCity] = useState("Select city");
  const [cityQuery, setCityQuery] = useState("");
  const [cities, setCities] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const closeTimeoutRef = useRef(null);

  const handleLogout = useCallback(async () => {
    setMobileOpen(false);
    const result = await logout();
    if (!result.ok) {
      toast.error(result.error || "Could not sign out. Please try again.");
      return;
    }
    navigate("/", { replace: true });
    toast.success("Logged out successfully");
  }, [logout, navigate]);

  const handleMouseEnter = useCallback((item) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setHovered(item);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setHovered(null);
    }, 300);
  }, []);

  useEffect(() => {
    let active = true;
    loadDiscoveryCities()
      .then((items) => { if (active) setCities(items); })
      .catch(() => { if (active) setCities([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let lastKnownScrollY = 0;
    let ticking = false;
    const handleScroll = () => {
      lastKnownScrollY = window.scrollY;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const isScrolled = lastKnownScrollY > 20;
          setScrolled((prev) => (prev !== isScrolled ? isScrolled : prev));
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setHovered(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);

  return (
    <header
      className={`premium-navbar sticky top-0 z-40 w-full transition-all duration-200 ${
        scrolled
          ? "bg-white/98 border-b border-slate-200/90 shadow-md backdrop-blur-md text-[#1E293B]"
          : "bg-white border-b border-slate-200/80 shadow-xs backdrop-blur-md text-[#1E293B]"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] w-full mx-auto px-3 sm:px-5 lg:px-6 h-16 flex items-center justify-between gap-1.5 lg:gap-3 xl:gap-4 relative select-none">

        {/* Left Section: Brand Logo + City Selector */}
        <div className="flex items-center gap-2 lg:gap-3.5 shrink-0">
          <Link
            to="/"
            className="group flex items-center shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B8842F] rounded-lg p-0.5"
            data-testid="brand-link"
            aria-label="ShreeHomes home"
          >
            <BrandLogo size="sm" subtitle="The real place for real estate" />
          </Link>

          {/* Desktop City selector */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger
              className="hidden md:inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-[#B8842F] transition-colors px-1.5 lg:px-2 py-1 focus:outline-none"
              data-testid="city-selector-trigger"
              aria-haspopup="true"
              aria-label={`Current city ${city}. Click to select a location`}
            >
              <MapPin className="w-3.5 h-3.5 text-[#B8842F] shrink-0" />
              <span className="truncate max-w-[85px]">{city}</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 opacity-80 shrink-0" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {cities.map((cityOption) => (
                <DropdownMenuItem
                  key={cityOption.slug}
                  onClick={() => {
                    const queryValue = cityOption.query_value || cityOption.name;
                    setCity(cityOption.name);
                    setCityQuery(queryValue);
                    navigate(`/properties?city=${encodeURIComponent(queryValue)}`);
                  }}
                  data-testid={`city-opt-${cityOption.slug}`}
                >
                  {cityOption.name}
                </DropdownMenuItem>
              ))}
              {cities.length === 0 && <DropdownMenuItem onClick={() => navigate("/properties")}>Browse all properties</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Center Section: Primary Navigation Links */}
        <nav
          className="hidden lg:flex items-center justify-center flex-1 min-w-0 gap-1 lg:gap-2.5 xl:gap-4 mx-1"
          data-testid="primary-nav"
          onMouseLeave={handleMouseLeave}
        >
          {NAV_ITEMS.map((item) => (
            <div
              key={item}
              className="py-3 shrink-0"
              onMouseEnter={() => handleMouseEnter(item)}
              onMouseLeave={handleMouseLeave}
            >
              <button
                type="button"
                onClick={() => setHovered((open) => (open === item ? null : item))}
                onFocus={() => handleMouseEnter(item)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    setHovered(null);
                    event.currentTarget.focus();
                  }
                }}
                className={`px-1.5 xl:px-2.5 py-1 inline-flex items-center gap-1 text-xs xl:text-[13px] font-semibold tracking-tight whitespace-nowrap transition-colors focus:outline-none ${
                  hovered === item
                    ? "text-[#9A6C27] font-bold"
                    : "text-slate-800 hover:text-[#9A6C27]"
                }`}
                data-testid={`nav-${item.replace(/[\s/]/g, "-").toLowerCase()}`}
                aria-haspopup="true"
                aria-expanded={hovered === item}
                aria-controls={`mega-menu-${item.replace(/[\s/]/g, "-").toLowerCase()}`}
              >
                <span>{item}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${hovered === item ? "rotate-180 text-[#9A6C27]" : ""}`} />
              </button>
            </div>
          ))}
        </nav>

        {/* Mega Menu Dropdown Card */}
        <AnimatePresence>
          {hovered && MEGA[hovered] && (
            <div
              id={`mega-menu-${hovered.replace(/[\s/]/g, "-").toLowerCase()}`}
              className="absolute left-1/2 -translate-x-1/2 top-full pt-2 z-50 pointer-events-auto"
              style={{ width: "max-content", maxWidth: "92vw" }}
              onMouseEnter={() => {
                if (closeTimeoutRef.current) {
                  clearTimeout(closeTimeoutRef.current);
                  closeTimeoutRef.current = null;
                }
              }}
              onMouseLeave={handleMouseLeave}
            >
              <MegaPanel item={hovered} onClose={() => setHovered(null)} cities={cities} />
            </div>
          )}
        </AnimatePresence>

        {/* Right Section: Action Suite (Post Property -> Notifications -> User Menu -> Hamburger) */}
        <div className="ml-auto flex items-center gap-2 lg:gap-3 xl:gap-4 shrink-0">

          {/* Post Property Button */}
          <Link
            to="/dashboard/property/new?type=land"
            className="hidden sm:inline-flex items-center gap-1.5 bg-gradient-to-r from-[#B8842F] to-[#8A6122] text-white rounded-full h-9 px-3.5 text-xs xl:text-[13px] font-bold shadow-xs hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all shrink-0 focus:outline-none"
            data-testid="post-property-btn"
          >
            <span>Post Property</span>
            <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-[#10b981] text-white shrink-0 uppercase tracking-wider">
              FREE
            </span>
          </Link>

          {/* Location Pin Utility Icon */}
          <button
            type="button"
            className="hidden lg:inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-700 hover:text-[#B8842F] transition-colors shrink-0 focus:outline-none"
            aria-label={`Browse properties in ${city}`}
            title={`Browse properties in ${city}`}
            data-testid="util-location"
            onClick={() => navigate(cityQuery ? `/properties?city=${encodeURIComponent(cityQuery)}` : "/properties")}
          >
            <MapPin className="w-4.5 h-4.5" />
          </button>

          {/* Notification Bell Component */}
          <NotificationBell
            buttonClass="inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-700 hover:text-[#B8842F] transition-colors relative shrink-0"
          />

          {/* User Profile Avatar / Account Menu Trigger (Clean Avatar A + Chevron, exactly like mockup) */}
          {user ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 shrink-0 focus:outline-none transition-opacity hover:opacity-90"
                  data-testid="user-menu-trigger"
                  aria-haspopup="true"
                  aria-label="Open account menu"
                >
                  <div className="w-7 h-7 xl:w-8 xl:h-8 rounded-full bg-[#B8842F] text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-2xs">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 p-1.5 shadow-xl border-slate-200" data-testid="user-menu">
                <div className="px-2.5 py-1.5 text-xs font-bold text-slate-800 border-b border-slate-100 mb-1">
                  Hi, {user.name?.split(" ")[0]}
                </div>
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard" className="rounded-md">
                  <LayoutDashboard className="w-4 h-4 mr-2 text-slate-500" />
                  <span>Dashboard</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard/saved")} data-testid="menu-saved" className="rounded-md">
                  <Heart className="w-4 h-4 mr-2 text-slate-500" />
                  <span>Saved</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  data-testid="menu-logout"
                  className="rounded-md text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  {isLoggingOut ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin text-emerald-500" />
                      <span>Signing out...</span>
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4 mr-2" />
                      <span>Logout</span>
                    </>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 shrink-0 focus:outline-none transition-opacity hover:opacity-90"
                  data-testid="user-menu-trigger"
                  aria-haspopup="true"
                  aria-label="Open account menu"
                >
                  <div className="w-7 h-7 xl:w-8 xl:h-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center shrink-0 border border-slate-200">
                    <User className="w-4 h-4 text-slate-600" />
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 p-3 shadow-xl border-slate-200" data-testid="user-menu">
                <p className="text-xs font-bold text-slate-800 mb-3">Welcome to ShreeHomes</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button asChild className="w-full text-white font-bold" style={{ background: BRAND }} data-testid="auth-register-btn">
                    <Link to="/register" onClick={() => setHovered(null)}>Register</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full border-[#B8842F] text-[#9A6C27] hover:bg-amber-50 font-bold" data-testid="auth-login-btn">
                    <Link to="/login" onClick={() => setHovered(null)}>Login</Link>
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-3 text-center">Save searches • Get recommendations</p>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Mobile & Tablet Hamburger Menu Trigger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-700 hover:bg-slate-100 transition-colors shrink-0 focus:outline-none"
                aria-label="Toggle mobile navigation menu"
                data-testid="hamburger-btn"
              >
                <Menu className="w-5 h-5 text-slate-700" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] sm:w-[360px] max-w-[90vw] p-0 overflow-y-auto" data-testid="mobile-drawer">
              <SheetHeader className="px-5 py-4 border-b border-slate-200">
                <SheetTitle className="text-left">
                  <BrandLogo size="sm" subtitle="Real place for real estate" />
                </SheetTitle>
                <SheetDescription className="text-left text-xs text-slate-500">Verified property discovery, made simple.</SheetDescription>
              </SheetHeader>

              <div className="px-5 py-4 border-b border-slate-200">
                {user ? (
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center gap-3" data-testid="m-user-info">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-xs shrink-0" style={{ background: BRAND }}>
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-800">{user.name}</p>
                      <p className="text-[11px] text-slate-500">View dashboard</p>
                    </div>
                  </Link>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Button asChild className="w-full text-white font-bold" style={{ background: BRAND }} data-testid="m-register">
                      <Link to="/register" onClick={() => setMobileOpen(false)}>Register</Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full border-[#B8842F] text-[#9A6C27] font-bold" data-testid="m-login">
                      <Link to="/login" onClick={() => setMobileOpen(false)}>Login</Link>
                    </Button>
                  </div>
                )}
              </div>

              <div className="px-5 py-3 border-b border-slate-200 space-y-1">
                {[
                  { label: "My Activity", to: "/dashboard" },
                  { label: "Recently Searched", to: "/properties" },
                  { label: "Recently Viewed", to: "/properties" },
                  { label: "Shortlisted", to: "/dashboard/saved" },
                  { label: "Contacted", to: "/dashboard" },
                ].map((q) => (
                  <Link
                    key={q.label}
                    to={q.to}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between py-2 text-xs font-medium text-slate-700 hover:text-[#9A6C27]"
                    data-testid={`m-quick-${q.label.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <span>{q.label}</span>
                    <ChevronRight className="w-4 h-4 opacity-50" />
                  </Link>
                ))}
                <Link
                  to="/dashboard/properties/new"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between py-2 text-xs font-bold"
                  style={{ color: BRAND }}
                  data-testid="m-post-property"
                >
                  <span className="inline-flex items-center gap-2">
                    Post Property
                    <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-emerald-500 text-white">FREE</span>
                  </span>
                  <ChevronRight className="w-4 h-4 opacity-50" />
                </Link>
              </div>

              <Accordion type="single" collapsible className="px-3">
                {[
                  { key: "buyers", label: "For Buyers", items: [
                    { l: "Buy a Home", to: "/properties" },
                    { l: "Plots on Demand", to: "/plot-on-demand" },
                    { l: "Commercial", to: "/properties?cat=Commercial" },
                    { l: "EMI Calculator", to: "/emi-calculator" },
                  ]},
                  { key: "tenants", label: "For Tenants", items: [
                    { l: "Rent a Home", to: "/properties?intent=Rent" },
                    { l: "PG / Co-living", to: "/properties?intent=Rent&type=PG" },
                  ]},
                  { key: "owners", label: "For Owners", items: [
                    { l: "Post Property FREE", to: "/dashboard/property/new?type=land" },
                    { l: "My Listings", to: "/dashboard/properties" },
                  ]},
                  { key: "dealers", label: "For Dealers/Builders", items: [
                    { l: "Agent Profiles", to: "/agents" },
                    { l: "Office Profiles", to: "/offices" },
                    { l: "Leads Dashboard", to: "/dashboard/leads" },
                  ]},
                ].map((sec) => (
                  <AccordionItem key={sec.key} value={sec.key} className="border-b-slate-200">
                    <AccordionTrigger className="text-xs font-semibold text-slate-800 hover:no-underline py-3 px-2" data-testid={`m-acc-${sec.key}`}>
                      {sec.label}
                    </AccordionTrigger>
                    <AccordionContent className="pb-2">
                      <div className="px-2 space-y-2">
                        {sec.items.map((it) => (
                          <Link
                            key={it.l}
                            to={it.to}
                            onClick={() => setMobileOpen(false)}
                            className="block py-1.5 text-xs text-slate-600 hover:text-[#9A6C27]"
                            data-testid={`m-acc-${sec.key}-${it.l.replace(/[\s/]/g, "-").toLowerCase()}`}
                          >
                            {it.l}
                          </Link>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {user && (
                <button
                  type="button"
                  disabled={isLoggingOut}
                  onClick={handleLogout}
                  className="w-full text-left px-5 py-4 text-xs font-bold border-t border-slate-200 text-red-600 flex items-center gap-2 disabled:opacity-60"
                  data-testid="m-logout"
                >
                  {isLoggingOut ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                      <span>Signing out...</span>
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </>
                  )}
                </button>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
